import type { LitterWhereInput } from '@gql/graphql';
import type { LitterListPageFormValues } from '@presentation/pages';
import { intersection } from 'lodash/fp';
import { useCallback } from 'react';

export const useLitterWhereBuilder = () => {
  const litterWhereBuilder = useCallback((form?: LitterListPageFormValues): LitterWhereInput => {
    const whereInputs: LitterWhereInput[] = [];

    whereInputs.push({
      breeder: { is: { certified: { equals: true }, public: { equals: true } } },
      adminValidated: { equals: true },
    });

    whereInputs.push({
      OR: [{ archived: { equals: false } }, { archived: { equals: null } }],
    });

    if (form) {
      if (form.price) {
        whereInputs.push({
          OR: [
            {
              AND: [
                { price: { gte: form.price[0], lte: form.price[1] } },
                { fixedPrice: { equals: true } },
              ],
            },
            {
              AND: [
                { fixedPrice: { equals: false } },
                {
                  priceFrom: {
                    gte: form.price[0],
                  },
                },
                {
                  priceTo: {
                    lte: form.price[1],
                  },
                },
              ],
            },
          ],
        });
      }

      if (form.breedNames?.length) {
        whereInputs.push({
          dogBreed: {
            is: {
              OR: [{ name: { in: form.breedNames } }, { baseBreedName: { in: form.breedNames } }],
            },
          },
        });
      }

      if (form.sizeIds?.length) {
        whereInputs.push({
          dogBreed: {
            is: {
              sizeCategory: {
                in: form.sizeIds,
              },
            },
          },
        });
      }

      if (form.born != null) {
        whereInputs.push({
          born: { equals: form.born },
        });
      }

      if (form.pedigree?.length) {
        const hasFci = !!intersection(form.pedigree, ['id1', 'FCI', 'fci']).length;
        const hasOther = form.pedigree.includes('OTHER');
        if (hasFci && hasOther) {
          whereInputs.push({ pedigree: { not: { equals: null } } });
        } else if (hasFci) {
          whereInputs.push({ pedigree: { in: ['FCI', 'fci', 'id1'] } });
        } else if (hasOther) {
          whereInputs.push({
            pedigree: { not: { in: ['', 'FCI', 'fci', 'id1'] } },
          });
          whereInputs.push({
            pedigree: { not: { equals: null } },
          });
        }
      }

      if (form.spokenLanguages?.length) {
        whereInputs.push({
          breeder: {
            is: { spokenLanguages: { hasSome: form.spokenLanguages } },
          },
        });
      }

      if (form.countries?.length) {
        whereInputs.push({
          breeder: {
            is: { account: { is: { address: { is: { country: { in: form.countries } } } } } },
          },
        });
      }

      if (form.regions?.length) {
        whereInputs.push({
          breeder: {
            is: { account: { is: { address: { is: { region: { in: form.regions } } } } } },
          },
        });
      }
    }

    return { ...(whereInputs.length ? { AND: whereInputs } : {}) } as LitterWhereInput;
  }, []);

  return { litterWhereBuilder };
};
